import { toast } from 'sonner';

import { errorIds, logError } from './log-error';

import { ApiError } from '@/services/api-client';

/**
 * Surfaces a refused workflow-state auto-save to the person losing the edits
 * (ESCLOUD-288). The debounced auto-save is invisible chrome, so a server
 * refusal — `403 subscription_read_only` for a degraded tenant, or
 * `403 attestation_required` — would otherwise drop a clinician's in-progress
 * sizing or report edits with nothing but a small "Save failed" indicator.
 *
 * Only refusals toast here. Transient errors keep their existing retry +
 * indicator behaviour — a network blip is not worth an interruption, and the
 * retry usually clears it.
 *
 * Stable toast ids: every further save attempt refreshes the same toast
 * instead of stacking a new one per keystroke.
 */
export function notifyWorkflowSaveRefused(error: Error): void {
    if (!(error instanceof ApiError) || error.status !== 403) return;

    logError(errorIds.WORKFLOW_SAVE_FORBIDDEN, 'Workflow-state save refused', {
        errorCode: error.errorCode,
        error,
    });

    if (error.errorCode === 'subscription_read_only') {
        toast.error('Your changes are not being saved', {
            id: 'workflow-save-read-only',
            duration: Infinity,
            description:
                'This workspace is read-only until its subscription is restored. Existing ' +
                'cases and reports remain available, but edits made here will be lost.',
        });
        return;
    }

    if (error.errorCode === 'attestation_required') {
        toast.error('Your changes are not being saved', {
            id: 'workflow-save-attestation',
            duration: Infinity,
            description:
                'Confirm your professional qualification to continue working. Reload the ' +
                'page to complete the confirmation step.',
        });
        return;
    }

    toast.error('Your changes could not be saved', {
        id: 'workflow-save-forbidden',
        description: error.message,
    });
}
